import React from 'react';
import { InlineMicButton } from '../InlineMicButton';
import { CodeSelector, CodeItem } from '../CodeSelector';
import type { BabySex, Hospital, ProcedureTemplate } from '../../types/clinical';

export type SurgeryCaseType = '' | 'elective' | 'emergency';

export interface SurgeryFormProps {
  surgProcedure: string;
  setSurgProcedure: (v: string) => void;
  surgHospital: string;
  setSurgHospital: (v: string) => void;
  surgDate: string;
  setSurgDate: (v: string) => void;
  surgEbl: string;
  setSurgEbl: (v: string) => void;
  surgAnaesthesiaType: string;
  setSurgAnaesthesiaType: (v: string) => void;
  surgAnaesthesiaOther: string;
  setSurgAnaesthesiaOther: (v: string) => void;
  surgAnaesthetist: string;
  setSurgAnaesthetist: (v: string) => void;
  surgCaseType: SurgeryCaseType;
  setSurgCaseType: (v: SurgeryCaseType) => void;
  surgSurgicalAssistant: string;
  setSurgSurgicalAssistant: (v: string) => void;
  surgIndications: string;
  setSurgIndications: React.Dispatch<React.SetStateAction<string>>;
  surgProcedureNotes: string;
  setSurgProcedureNotes: React.Dispatch<React.SetStateAction<string>>;
  surgFindings: string;
  setSurgFindings: React.Dispatch<React.SetStateAction<string>>;
  surgComplications: string;
  setSurgComplications: React.Dispatch<React.SetStateAction<string>>;
  surgPostOp: string;
  setSurgPostOp: React.Dispatch<React.SetStateAction<string>>;
  surgIcd10: CodeItem[];
  setSurgIcd10: (v: CodeItem[]) => void;
  hospitals: Hospital[];
  templates: ProcedureTemplate[];
  searchIcd10: (query: string) => CodeItem[];
  icd10Loading: boolean;
  pregnancyId?: string | null;
  surgBabyWeight?: number | null;
  setSurgBabyWeight?: (v: number | null) => void;
  surgBabySex?: BabySex;
  setSurgBabySex?: (v: BabySex) => void;
  surgApgar1?: number | null;
  setSurgApgar1?: (v: number | null) => void;
  surgApgar5?: number | null;
  setSurgApgar5?: (v: number | null) => void;
  surgBcgGiven?: boolean;
  setSurgBcgGiven?: (v: boolean) => void;
  surgPolioGiven?: boolean;
  setSurgPolioGiven?: (v: boolean) => void;
  onSave: () => void;
  onCancel: () => void;
}

export function SurgeryForm({
  surgProcedure, setSurgProcedure,
  surgHospital, setSurgHospital,
  surgDate, setSurgDate,
  surgEbl, setSurgEbl,
  surgAnaesthesiaType, setSurgAnaesthesiaType,
  surgAnaesthesiaOther, setSurgAnaesthesiaOther,
  surgAnaesthetist, setSurgAnaesthetist,
  surgCaseType, setSurgCaseType,
  surgSurgicalAssistant, setSurgSurgicalAssistant,
  surgIndications, setSurgIndications,
  surgProcedureNotes, setSurgProcedureNotes,
  surgFindings, setSurgFindings,
  surgComplications, setSurgComplications,
  surgPostOp, setSurgPostOp,
  surgIcd10, setSurgIcd10,
  hospitals, templates,
  searchIcd10, icd10Loading,
  pregnancyId,
  surgBabyWeight, setSurgBabyWeight,
  surgBabySex, setSurgBabySex,
  surgApgar1, setSurgApgar1,
  surgApgar5, setSurgApgar5,
  surgBcgGiven, setSurgBcgGiven,
  surgPolioGiven, setSurgPolioGiven,
  onSave, onCancel,
}: SurgeryFormProps) {
  return (
    <div className="mb-4 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      {/* Procedure template selector — applies structured fields */}
      {templates.filter(t => t.templateType === 'surgery' || !t.templateType).length > 0 && (
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-500 mb-1">Load from Template</label>
          <select
            value=""
            onChange={e => {
              const tmpl = templates.find(t => t.id === e.target.value);
              if (tmpl) {
                setSurgProcedure(tmpl.name);
                if (tmpl.anaesthesiaType) setSurgAnaesthesiaType(tmpl.anaesthesiaType);
                if (tmpl.indications) setSurgIndications(tmpl.indications);
                if (tmpl.procedureNotes) setSurgProcedureNotes(tmpl.procedureNotes);
                if (tmpl.postOpInstructions) setSurgPostOp(tmpl.postOpInstructions);
                if (tmpl.defaultIcd10Codes?.length) {
                  setSurgIcd10(tmpl.defaultIcd10Codes.map(c => ({ code: c, description: '' })));
                }
              }
            }}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white"
          >
            <option value="">— Select a template —</option>
            {templates.filter(t => t.templateType === 'surgery' || !t.templateType).map(t => (
              <option key={t.id} value={t.id}>{t.name}{t.category ? ` (${t.category})` : ''}</option>
            ))}
          </select>
        </div>
      )}
      {/* Row 1: Procedure, Hospital */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Procedure *</label>
          <input value={surgProcedure} onChange={e => setSurgProcedure(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Hospital</label>
          <select value={surgHospital} onChange={e => setSurgHospital(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white">
            <option value="">— Select —</option>
            {hospitals.map(h => <option key={h.id} value={h.name}>{h.name}</option>)}
          </select>
        </div>
      </div>
      {/* Row 2: Date, EBL */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Date</label>
          <input type="date" value={surgDate} onChange={e => setSurgDate(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">EBL (ml)</label>
          <input value={surgEbl} onChange={e => setSurgEbl(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
        </div>
      </div>
      {/* Row 3: Anaesthesia Type, Anaesthetist */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Anaesthesia Type</label>
          <select value={surgAnaesthesiaType} onChange={e => { setSurgAnaesthesiaType(e.target.value); if (e.target.value !== 'Other') setSurgAnaesthesiaOther(''); }} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white">
            <option value="">— Select —</option>
            <option value="General">General</option>
            <option value="Spinal">Spinal</option>
            <option value="Saddle">Saddle</option>
            <option value="Conscious Sedation">Conscious Sedation</option>
            <option value="Epidural">Epidural</option>
            <option value="Other">Other</option>
          </select>
          {surgAnaesthesiaType === 'Other' && (
            <input value={surgAnaesthesiaOther} onChange={e => setSurgAnaesthesiaOther(e.target.value)} placeholder="Specify anaesthesia type" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded mt-1" />
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Anaesthetist Name</label>
          <input value={surgAnaesthetist} onChange={e => setSurgAnaesthetist(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
        </div>
      </div>
      {/* Row 4: Case Type, Surgical Assistant */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Case Type</label>
          <select
            value={surgCaseType}
            onChange={e => setSurgCaseType(e.target.value as SurgeryCaseType)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white"
          >
            <option value="">— Select —</option>
            <option value="elective">Elective</option>
            <option value="emergency">Emergency</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Surgical Assistant</label>
          <input
            value={surgSurgicalAssistant}
            onChange={e => setSurgSurgicalAssistant(e.target.value)}
            placeholder="Assistant name (optional)"
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
          />
        </div>
      </div>
      {/* Indications (+ mic) */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-medium text-gray-500">Indications</label>
          <InlineMicButton onTranscript={t => setSurgIndications(prev => prev ? prev + ' ' + t : t)} />
        </div>
        <textarea value={surgIndications} onChange={e => setSurgIndications(e.target.value)} rows={4} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
      </div>
      {/* Procedure Notes (+ mic) */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-medium text-gray-500">Procedure Notes</label>
          <InlineMicButton onTranscript={t => setSurgProcedureNotes(prev => prev ? prev + ' ' + t : t)} />
        </div>
        <textarea value={surgProcedureNotes} onChange={e => setSurgProcedureNotes(e.target.value)} rows={8} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
      </div>
      {/* Findings (+ mic) */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-medium text-gray-500">Findings</label>
          <InlineMicButton onTranscript={t => setSurgFindings(prev => prev ? prev + ' ' + t : t)} />
        </div>
        <textarea value={surgFindings} onChange={e => setSurgFindings(e.target.value)} rows={8} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
      </div>
      {/* ICD-10 Codes (optional) */}
      <div className="mb-3">
        <CodeSelector
          codeType="icd10"
          selected={surgIcd10}
          onChange={setSurgIcd10}
          searchFn={searchIcd10}
          cacheLoading={icd10Loading}
          label="ICD-10 Codes (optional)"
          placeholder="Search ICD-10 codes..."
        />
      </div>
      {/* Complications (+ mic) */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-medium text-gray-500">Complications</label>
          <InlineMicButton onTranscript={t => setSurgComplications(prev => prev ? prev + ' ' + t : t)} />
        </div>
        <textarea value={surgComplications} onChange={e => setSurgComplications(e.target.value)} rows={8} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
      </div>
      {/* Post-Op Instructions (+ mic) */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-medium text-gray-500">Post-Op Instructions</label>
          <InlineMicButton onTranscript={t => setSurgPostOp(prev => prev ? prev + ' ' + t : t)} />
        </div>
        <textarea value={surgPostOp} onChange={e => setSurgPostOp(e.target.value)} rows={8} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
      </div>
      {/* Baby Details — shown only for obstetric surgeries (pregnancyId set) */}
      {pregnancyId && setSurgBabyWeight && setSurgBabySex && setSurgApgar1 && setSurgApgar5 && setSurgBcgGiven && setSurgPolioGiven && (
        <div className="mb-3">
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">Baby Details</label>
          <div className="grid grid-cols-4 gap-3 mb-2">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Weight (g)</label>
              <input type="number" step={10} min={0} max={6000} value={surgBabyWeight ?? ''} onChange={e => setSurgBabyWeight(e.target.value ? parseInt(e.target.value) : null)} placeholder="e.g. 3200" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Sex</label>
              <select value={surgBabySex || ''} onChange={e => setSurgBabySex(e.target.value as BabySex)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white">
                <option value="">— Select —</option><option value="male">Male</option><option value="female">Female</option><option value="unknown">Unknown</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Apgar 1 min</label>
              <input type="number" min={0} max={10} value={surgApgar1 ?? ''} onChange={e => setSurgApgar1(e.target.value ? parseInt(e.target.value) : null)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Apgar 5 min</label>
              <input type="number" min={0} max={10} value={surgApgar5 ?? ''} onChange={e => setSurgApgar5(e.target.value ? parseInt(e.target.value) : null)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
            </div>
          </div>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={surgBcgGiven || false} onChange={e => setSurgBcgGiven(e.target.checked)} className="h-4 w-4 rounded" /> BCG Given</label>
            <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={surgPolioGiven || false} onChange={e => setSurgPolioGiven(e.target.checked)} className="h-4 w-4 rounded" /> Polio Given</label>
          </div>
        </div>
      )}
      <div className="flex gap-2">
        <button onClick={onSave} disabled={!surgProcedure} className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">Save</button>
        <button onClick={onCancel} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
      </div>
    </div>
  );
}
